import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GameProps, GameDef, playAreaStyle } from './types';

// ── MEMORY MATCH ─────────────────────────────────────────────────────────────
// 4x3 grid of face-down cards; flip two to find matching pairs. No timer —
// the game ends when every pair is found. Score = pairs found (6).

const FACES = ['⭐', '🚀', '🎁', '🦄', '🐉', '🌈', '🍭', '⚡', '🎈', '🦖'];
const PAIRS = 6;

interface Card {
  id: number;
  face: string;
  flipped: boolean;
  matched: boolean;
}

// Deterministic-enough shuffle; randomness varies per mount which is fine here.
function buildDeck(): Card[] {
  const faces = [...FACES].sort(() => Math.random() - 0.5).slice(0, PAIRS);
  const deck = [...faces, ...faces]
    .map((face, i) => ({ id: i, face, flipped: false, matched: false }))
    .sort(() => Math.random() - 0.5);
  return deck;
}

function MemoryMatch({ theme, isKid, onEnd }: GameProps) {
  const [deck, setDeck] = useState<Card[]>(buildDeck);
  const [pairsFound, setPairsFound] = useState(0);
  const [locked, setLocked] = useState(false);
  const pickedRef = useRef<number[]>([]);
  const endedRef = useRef(false);

  useEffect(() => {
    if (pairsFound === PAIRS && !endedRef.current) {
      endedRef.current = true;
      const t = setTimeout(() => onEnd({ score: PAIRS, noun: 'pairs' }), 700);
      return () => clearTimeout(t);
    }
  }, [pairsFound, onEnd]);

  const flip = useCallback((idx: number) => {
    if (locked) return;
    const picked = pickedRef.current;
    if (picked.length >= 2 || picked.includes(idx)) return;

    setDeck(prev => {
      const card = prev[idx];
      if (card.flipped || card.matched) return prev;
      return prev.map((c, i) => (i === idx ? { ...c, flipped: true } : c));
    });

    const next = [...picked, idx];
    pickedRef.current = next;

    if (next.length === 2) {
      setLocked(true);
      const [a, b] = next;
      setDeck(cur => {
        const isMatch = cur[a].face === cur[b].face;
        setTimeout(() => {
          setDeck(d => d.map((c, i) =>
            i === a || i === b
              ? isMatch
                ? { ...c, matched: true }
                : { ...c, flipped: false }
              : c
          ));
          if (isMatch) setPairsFound(p => p + 1);
          pickedRef.current = [];
          setLocked(false);
        }, isMatch ? 450 : 850);
        return cur;
      });
    }
  }, [locked]);

  return (
    <>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        fontWeight: 800, fontSize: isKid ? '1.3rem' : '1.1rem',
      }}>
        <span>🧠 Memory Match</span>
        <span style={{ color: theme.accent }}>pairs {pairsFound}/{PAIRS}</span>
      </div>

      <div style={{
        ...playAreaStyle(theme),
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gridTemplateRows: 'repeat(3, 1fr)',
        gap: '8px',
        padding: '12px',
      }}>
        {deck.map((card, idx) => {
          const faceUp = card.flipped || card.matched;
          return (
            <button
              key={card.id}
              onClick={() => flip(idx)}
              aria-label={faceUp ? card.face : 'Face-down card'}
              style={{
                background: faceUp ? `${theme.bg}` : `linear-gradient(135deg, ${theme.accent}, ${theme.btn})`,
                border: `2px solid ${card.matched ? '#22c55e' : theme.light}`,
                borderRadius: '12px',
                cursor: faceUp ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isKid ? '2.2rem' : '1.9rem',
                lineHeight: 1,
                opacity: card.matched ? 0.6 : 1,
                transition: 'background 0.2s ease, opacity 0.3s ease',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {faceUp ? <span style={{ animation: 'reward-pop-in 0.2s ease-out' }}>{card.face}</span> : '❓'}
            </button>
          );
        })}
      </div>
    </>
  );
}

export const MEMORY_MATCH: GameDef = {
  id: 'memory-match',
  title: 'Memory Match',
  emoji: '🧠',
  instructions: 'Flip two cards at a time. Find all the matching pairs!',
  verb: 'Match',
  Component: MemoryMatch,
};

export default MemoryMatch;
